import { useEffect, useState, type FormEvent } from 'react';

import type { CustomerDetail } from '@domain/customer/Customer';
import type { Plan } from '@domain/plan/Plan';
import {
  InvalidCustomerEdit,
  changePlan as changePlanUseCase,
} from '@application/customer/editCustomer';
import { apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { HttpCustomerRepository } from '@infrastructure/api/HttpCustomerRepository';
import { HttpPlanRepository } from '@infrastructure/api/HttpPlanRepository';

import { Modal } from './Modal';

const customerRepo = new HttpCustomerRepository(apiClient);
const planRepo = new HttpPlanRepository(apiClient);

interface Props {
  open: boolean;
  customer: CustomerDetail | null;
  onClose: () => void;
  onSaved: (c: CustomerDetail) => void;
}

export function ChangePlanModal({ open, customer, onClose, onSaved }: Props) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    planRepo
      .list({ limit: 200 })
      .then((p) => {
        setPlans(p.rows);
        if (customer && !planId) setPlanId(customer.plan.id);
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!customer) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const updated = await changePlanUseCase(customerRepo)(
        customer.id, planId, reason,
      );
      onSaved(updated);
      onClose();
      setReason('');
    } catch (e) {
      setError(
        e instanceof InvalidCustomerEdit ? e.message : errorText(e),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title={`Change plan — ${customer.company_name}`} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3 text-sm">
        <div className="rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Current plan: <strong>{customer.plan.name}</strong>{' '}
          ({customer.plan.plan_type} · ${customer.plan.base_rate}/SMS)
        </div>
        <label className="block">
          New plan
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
            required
          >
            <option value="">— pick a plan —</option>
            {plans
              .filter((p) => p.is_active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.plan_type}) — ${p.base_rate}/SMS · ${p.monthly_fee}/mo
                </option>
              ))}
          </select>
        </label>
        <label className="block">
          Reason (logged in audit trail)
          <textarea
            required minLength={3} rows={3}
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1">
            Cancel
          </button>
          <button
            type="submit" disabled={submitting || !planId}
            className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50"
          >
            {submitting ? 'Applying…' : 'Change plan'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
